import { useEffect, useState } from "react";
import { api, apiError, money, fileUrl } from "@/lib/api";
import { payWithProvider } from "@/lib/payments";
import { useAuth } from "@/context/AuthContext";
import { Loader, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, Star, Download, Receipt, Boxes } from "lucide-react";
import { cn } from "@/lib/utils";

export default function Subscription() {
  const { refresh } = useAuth();
  const [plans, setPlans] = useState(null);
  const [me, setMe] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = () => {
    api.get("/plans").then((r) => setPlans(r.data.filter((p) => p.segment === "manufacturer")));
    api.get("/subscription/me").then((r) => setMe(r.data)).catch(() => setMe({}));
    api.get("/subscription/history").then((r) => setHistory(r.data)).catch(() => setHistory([]));
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (!sid) return;
    let n = 0;
    const poll = async () => {
      n += 1;
      try { const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { toast.success("Subscription activated"); await refresh(); load(); window.history.replaceState({}, "", "/manufacturer/subscription"); return; } } catch {}
      if (n < 10) setTimeout(poll, 2000);
    };
    poll();
  }, [refresh]);

  if (!plans || !me) return <Loader />;
  const current = me.plan_id;

  async function choose(p) {
    setBusy(p.id);
    try {
      if (p.contact) { window.location.href = "/pricing"; return; }
      if (p.price > 0) {
        const { data } = await api.post("/payments/subscribe", { plan_id: p.id, origin_url: window.location.origin });
        await payWithProvider(data, api, { onSuccess: async () => { toast.success("Subscription activated"); await refresh(); load(); }, onError: (m) => toast.error(m) });
      }
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  }

  async function cancel() {
    if (!window.confirm("Cancel your subscription? You'll move to pay-as-you-go commission.")) return;
    try { await api.post("/subscription/cancel"); toast.success("Subscription cancelled"); await refresh(); load(); }
    catch (e) { toast.error(apiError(e)); }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-forest">Subscription</h1>
          <p className="text-sm text-ink/60">Current plan: <b className="text-forest">{plans.find((p) => p.id === current)?.name || "No active plan"}</b> · Commission <span className="num text-forest">{me.transaction_fee_pct}%</span></p>
        </div>
        {current && <Button data-testid="cancel-sub" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" onClick={cancel}>Cancel plan</Button>}
      </div>

      <div className="rounded-xl border border-border bg-psage/20 p-4 text-sm text-ink/70">
        <span className="inline-flex items-center gap-2 text-forest"><Boxes className="h-4 w-4" /> Product usage:</span>{" "}
        <b className="num text-forest">{me.product_count}</b>{" of "}
        <b className="num text-forest">{me.product_limit === null ? "Unlimited" : me.product_limit}</b>{" products used"}
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        {plans.sort((a, b) => a.order - b.order).map((p) => (
          <div key={p.id} data-testid={`plan-${p.id}`} className={cn("relative flex flex-col rounded-xl border bg-card p-6 shadow-sm",
            current === p.id ? "border-2 border-forest" : "border-border")}>
            {p.recommended && <span className="absolute right-4 top-4 inline-flex items-center gap-1 rounded-full bg-lime px-2 py-0.5 text-xs font-semibold text-ink"><Star className="h-3 w-3" /> Popular</span>}
            <p className="font-heading text-lg font-semibold text-forest">{p.name}</p>
            <p className="mt-0.5 h-9 text-xs text-ink/50">{p.target}</p>
            <div className="mt-1 num text-3xl font-semibold text-forest">
              {p.price === null ? "Custom" : p.price === 0 ? "₹0" : money(p.price).replace(".00", "")}
              {p.price ? <span className="text-sm font-normal text-ink/50">/mo</span> : null}
            </div>
            {p.transaction_fee_pct != null && <p className="mt-1 text-sm text-ink/60">Commission <b className="num text-forest">{p.transaction_fee_pct}%</b></p>}
            <ul className="mt-4 flex-1 space-y-2 text-sm">
              {p.features.map((f) => <li key={f} className="flex items-start gap-2 text-ink/70"><Check className="mt-0.5 h-4 w-4 shrink-0 text-olive" /> {f}</li>)}
            </ul>
            <div className="mt-5">
              {current === p.id ? <Button className="w-full bg-psage text-forest" disabled>Current plan</Button> :
                p.contact ? <Button data-testid={`choose-${p.id}`} variant="outline" className="w-full border-forest text-forest hover:bg-psage" onClick={() => choose(p)}>Contact Sales</Button> : (
                <Button data-testid={`choose-${p.id}`} className="w-full bg-forest text-white hover:bg-forest/90" disabled={busy === p.id} onClick={() => choose(p)}>{p.cta}</Button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div>
        <h2 className="font-heading text-lg font-semibold text-forest">Billing history</h2>
        {history.length === 0 ? <div className="mt-3"><EmptyState title="No payments yet" hint="Your subscription invoices will appear here." icon={Receipt} /></div> : (
          <div className="mt-3 overflow-x-auto rounded-xl border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-psage/40 text-left"><tr className="overline text-forest"><th className="px-4 py-3">Date</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Invoice</th></tr></thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} data-testid={`bill-${h.id}`} className="border-t border-border">
                    <td className="px-4 py-3 num text-ink/70">{(h.created_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-3">{h.plan_name}</td>
                    <td className="px-4 py-3 num text-forest">{money(h.amount)}</td>
                    <td className="px-4 py-3">{h.invoice_id ? <a data-testid={`bill-inv-${h.id}`} href={fileUrl(`/api/invoices/${h.invoice_id}/pdf`)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-forest hover:underline"><Download className="h-4 w-4" /> PDF</a> : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
